import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { List } from '@components/integrations/integrations-types';
import { useListSidebar } from '@components/integrations/lists';
import { showError } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

export const listUrlParam = 'list';

const shortIdPattern = /^[A-Za-z0-9]+$/;

export const useOpenListFromUrl = (lists: List[] | undefined) => {
  const { t } = useScopedTranslate(locale);
  const [searchParams, setSearchParams] = useSearchParams();
  const listId = searchParams.get(listUrlParam);
  const showSidebar = useListSidebar();
  const hasHandledRef = useRef(false);
  const frameRef = useRef<number>();

  useEffect(() => {
    return () => {
      if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  useEffect(() => {
    if (!listId || !lists || hasHandledRef.current) return;

    hasHandledRef.current = true;

    const isKnownList = shortIdPattern.test(listId) && lists.some((l) => l.shortId === listId);

    if (!isKnownList) {
      const remaining = new URLSearchParams(searchParams);
      remaining.delete(listUrlParam);
      setSearchParams(remaining, { replace: true });
      showError(t('listNotFound'));
      return;
    }

    frameRef.current = requestAnimationFrame(() => showSidebar({ listId }));
  }, [listId, lists, searchParams, setSearchParams, showSidebar, t]);
};

const locale = {
  en: {
    listNotFound: 'That list no longer exists',
  },
};
